import React, { useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { backendURL, useApiFetch } from "../../utils/api";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { useInfoDialog } from "../../contexts/InfoDialogContext";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { getShootingEventConfig } from "../../configs/utils/ShootingEventConfigFactory.ts";
import "./ManageCompetition.css";
import { Competition } from "../../types/Competition.ts";
import { ShootingSession } from "../../types/ShootingSession";
import { exportCompetitionReportToPdf } from "../../utils/exportCompetitionReportToPDF.ts";


function ManageCompetition() {
    const apiFetch = useApiFetch();
    const { getAccountId } = useAuth();
    const navigate = useNavigate();
    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const { confirm } = useConfirmation();
    const { success } = useNotifications();
    const { showInfo } = useInfoDialog();

    const handleGenerateReport = async (competition: Competition) => {
        try {
            const fetchSessions = async (competitionId: string, shootingEventId: string): Promise<ShootingSession[]> => {
                const params = new URLSearchParams({ competitionId, shootingEventId });
                return await apiFetch<ShootingSession[]>(`${backendURL}/shooting-sessions/competition-sessions?${params}`, {
                    method: "GET",
                    includeAuth: true,
                });
            };
            await exportCompetitionReportToPdf(competition, fetchSessions);
        } catch (err) {
            console.error("Error generating report:", err);
        }
    };

    const handleDeleteCompetition = async (competition: Competition) => {
        const confirmed = await confirm({ message: "Czy na pewno chcesz usunąć zawody?" });
        if (!confirmed) return;

        try {
            await apiFetch(`${backendURL}/competitions`, {
                method: "DELETE",
                includeAuth: true,
                body: JSON.stringify({
                    competitionId: competition.id,
                    partitionKey: competition.hostingClubId,
                }),
            });
            setCompetitions((prev) => prev.filter((c) => c.id !== competition.id));
            success("Zawody zostały usunięte");
        } catch (err) {
            console.error("Error deleting competition:", err);
        }
    };

    const handleChangeCompetitionVisibility = async (competition: Competition, isPublic: boolean) => {
        const reportUrl = `https://targetcalculator.pl/competition-reports/${competition.id}`;

        const confirmed = await confirm({
            message: `Czy na pewno chcesz ${isPublic ? "opublikować wyniki?" : `ukryć wyniki opublikowane pod adresem ${reportUrl} ?`}`,
            confirmLabel: "Tak",
            confirmColor: "#198754"
        });

        if (!confirmed) return;

        try {
            await apiFetch(`${backendURL}/competitions/${competition.id}/visibility`, {
                method: "PATCH",
                includeAuth: true,
                body: JSON.stringify({
                    isPublic: isPublic,
                }),
            });
            setCompetitions((prev) => prev.map((c) => c.id === competition.id ? { ...c, isPublic } : c));

            if (isPublic) {
                showInfo({
                    message: (
                        <>
                            Wyniki zostały opublikowane. Raport dostępny pod adresem:{' '}
                            <a href={reportUrl} target="_blank" rel="noopener noreferrer">{reportUrl}</a>
                        </>
                    )
                });
            } else {
                success("Wyniki zostały ukryte");
            }
        } catch (err) {
            console.error("Error changing competition visibility:", err);
        }
    };

    useEffect(() => {
        const fetchCompetitions = async () => {
            try {
                const data = await apiFetch(`${backendURL}/competitions/club-admin/${getAccountId()}`, {
                    method: "GET",
                    includeAuth: true
                });
                setCompetitions(data);
            } catch (err) {
                console.error("Error fetching competitions:", err);
            }
        };
        fetchCompetitions();
    }, []);

    const toggleExpanded = (competitionName: string) => {
        const newExpanded = new Set(expanded);
        if (newExpanded.has(competitionName)) {
            newExpanded.delete(competitionName);
        } else {
            newExpanded.add(competitionName);
        }
        setExpanded(newExpanded);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL');
    };

    const handleShowResults = (competition: Competition, shootingEventId: string) => {
        navigate("/manage-event-ranking", {
            state: {
                competitionId: competition.id,
                competitionDate: competition.date,
                competitionName: competition.name,
                shootingEventId: shootingEventId,
                shootingEventName: getShootingEventConfig(shootingEventId).shootingEventName,
            },
        });
    };

    return (
        <Container>
            <Row>
                <Col xl={8} md={10} xs={12} className="mx-auto">
                    <div className="py-3">
                        <h1 className="font-weight-light text-center mb-4">Lista zawodów</h1>
                        {competitions.map((competition, index) => (
                            <div key={index} className="mb-3">
                                <Row className="align-items-center">
                                    <Col lg={7} xs={12}>
                                        <div className="p-3 border border-dark border-1 rounded bg-light-subtle" onClick={() => toggleExpanded(competition.name)} style={{ cursor: 'pointer' }}>
                                            <strong>{competition.name}</strong> {formatDate(competition.date)}
                                        </div>
                                    </Col>
                                    <Col lg={5} xs={12} className="d-flex justify-content-around">
                                        <Button variant="secondary" size="sm" onClick={() => toggleExpanded(competition.name)}>
                                            Wyświetl konkurencje
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={() => handleGenerateReport(competition)}>
                                            Generuj raport
                                        </Button>
                                        { competition.isPublic ? (
                                            <Button variant="warning" size="sm" className="visibility-btn" onClick={() => handleChangeCompetitionVisibility(competition, false)}>
                                                Ukryj wyniki
                                            </Button>
                                        ) : (
                                            <Button variant="success" size="sm" className="visibility-btn" onClick={() => handleChangeCompetitionVisibility(competition, true)}>
                                                Opublikuj wyniki
                                            </Button>
                                        )}
                                        <Button variant="danger" size="sm" onClick={() => handleDeleteCompetition(competition)}>
                                            Usuń
                                        </Button>
                                    </Col>
                                </Row>
                                {expanded.has(competition.name) && (
                                    <div className="mt-2">
                                        {competition.shootingEventIds.map((eventId, eventIndex) => (
                                            <Row key={eventIndex} className="align-items-center ms-md-1 mb-1">
                                                <Col md={10} xs={12}>
                                                    <div
                                                        className="p-2 border border-dark border-1 rounded bg-light-subtle"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleShowResults(competition, eventId)}
                                                    >
                                                        {getShootingEventConfig(eventId).shootingEventName}
                                                    </div>
                                                </Col>
                                                <Col md={2} xs={12} className="d-flex justify-content-center justify-content-md-end">
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        className="show-results-btn"
                                                        onClick={() => handleShowResults(competition, eventId)}
                                                    >
                                                        Wyświetl wyniki
                                                    </Button>
                                                </Col>
                                            </Row>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default ManageCompetition;